import {
    EventCardActions,
    EventCardBadge,
    EventCardDate,
    EventCardDetails,
    EventCardDetailsSpacer,
    EventCardLink,
    EventCardLocation,
    EventCardPrice,
    EventCardPromoted,
    EventCardSaveAction,
    EventCardTitle,
    EventCardTracking,
    EventCardUrgencySignals,
    getEventCardLinkProps,
    getFormattedSalesStatus,
    HORIZONTAL_LEFT_CARD_BADGE_STYLE,
    useFormattedLocationString,
    useGetFormattedEventDateTimeString,
    useMinPriceString,
    VerticalEventCard,
    VERTICAL_EVENT_CARD_IMAGE_CONFIG,
} from '@eventbrite/event-renderer';
import React from 'react';
import { DiscoverEventCardProps } from '../../types';

import { EventCardMultiImage } from '../../Experiments/EventCardMultiImage';
import './DiscoverVerticalBrowseHierarchyEventCard.scss';

export const DiscoverVerticalBrowseHierarchyEventCard: React.FunctionComponent<
    DiscoverEventCardProps
> = (props) => {
    const {
        event,
        locale,
        isRaised,
        statsigLocationString,
        moreActions,
        affCode = 'ebdssbdestsearch',
        'data-testid': dataTestId,
        isExpansionsLoading,
        hidePriceDescription,
        bucketLabel,
    } = props;
    const { urgencySignals } = event;

    const linkProps = getEventCardLinkProps({
        affCode: affCode,
        url: event?.url,
        isTargetBlank: true,
        isNoFollow: event.ticketsBy !== 'Eventbrite',
        onClick: (e) => props.onClick?.(e),
    });

    const formattedDateString = useGetFormattedEventDateTimeString({
        startDate: event?.startDate,
        startTime: event?.startTime,
        timezone: event?.timezone,
        repeatingInstanceCount: event?.repeatingInstanceCount,
        locale: locale,
        useUserTimezone: event.isOnlineEvent,
        dateTimeDelimeter: '•',
    });

    const formattedPriceString = useMinPriceString({
        price: event?.minPrice,
        locale: locale,
    });

    const formattedLocationString = useFormattedLocationString({
        venue: event.venue,
        style: 'venue',
    });

    //Only show active Sales Status (sold out etc)
    //For non-repeating events to prevent incorrect signals.
    const formattedSalesStatus = getFormattedSalesStatus(event);

    const shouldShowUrgencySignals =
        !!urgencySignals && !formattedSalesStatus && event.isPromoted;

    const eventCardTrackingProps = {
        statsigLocationString: statsigLocationString,
        event,
        eventBucketLabel: bucketLabel?.toString(),
    };

    return (
        <section
            className="discover-vertical-event-card"
            data-testid={dataTestId}
        >
            <VerticalEventCard isRaised={isRaised} actionVisibility="hidden">
                <EventCardTracking {...eventCardTrackingProps}>
                    <EventCardBadge style={HORIZONTAL_LEFT_CARD_BADGE_STYLE}>
                        {formattedSalesStatus}
                    </EventCardBadge>
                    {shouldShowUrgencySignals && (
                        <EventCardUrgencySignals
                            className="discover-vertical-event-card__urgency-signals"
                            urgencySignals={urgencySignals}
                        />
                    )}
                    <EventCardMultiImage
                        event={event}
                        linkProps={linkProps}
                        isLazyImage={props.isLazyImage ? 'lazy' : 'eager'}
                        tokens={{
                            ...VERTICAL_EVENT_CARD_IMAGE_CONFIG,
                            '--image-background-color': event.edgeColor,
                        }}
                    />
                    <EventCardDetails
                        space="xs"
                        tokens={{
                            '--EventCardDetailsPadding': '16px 8px 12px 12px',
                            '--EventCardDetailsFlexGrow': '2',
                            '--EventCardDetailsPosition': 'relative',
                        }}
                    >
                        <EventCardLink {...linkProps}>
                            <EventCardTitle
                                className="event-card__clamp-line--two-browse eds-text-color--ui-800"
                                title={event.name}
                            />
                        </EventCardLink>
                        <EventCardDate
                            formattedDateString={formattedDateString}
                            variant="body-md-bold"
                        />

                        <EventCardLocation
                            className="event-card__clamp-line--one eds-text-color--ui-600"
                            formattedLocationString={formattedLocationString}
                            variant="body-md"
                        />
                        <EventCardDetailsSpacer />
                        <EventCardPrice
                            isLoading={isExpansionsLoading}
                            isFree={event.isFree}
                            hidePriceDescription={hidePriceDescription}
                            formattedPriceString={formattedPriceString}
                            className="eds-text-color--ui-800"
                            variant="body-md-bold"
                        />

                        <EventCardPromoted isPromoted={event.isPromoted} />

                        <EventCardActions
                            tokens={{
                                '--EventCardActionsPosition': 'absolute',
                                '--EventCardActionsTop': '-48px',
                                '--EventCardActionsRight': '8px',
                                '--EventCardActionsGap': '16px',
                            }}
                            actionStyleOverride="outline"
                        >
                            <EventCardSaveAction
                                isAuthenticated={!!props.isAuthenticated}
                                savedByYou={event.savedByYou}
                                id={event.id}
                                eventName={event.name}
                                imageUrl={event.imageUrl}
                            />

                            {moreActions}
                        </EventCardActions>
                    </EventCardDetails>
                </EventCardTracking>
            </VerticalEventCard>
        </section>
    );
};
